/**
 * UatPersonaSeedAdmin — reseed the UAT testers' personas from /admin (Victor, 15 Sep 2026).
 *
 * Each tester switches into personas of their own to use the app as a team member, a People
 * Leader or a One-Up. The reseed used to be a workstation script; since the seed route needs
 * more than a persona header it also takes a signed-in admin or hr_admin. The tab shows only
 * for those two roles and only where test personas exist, and the server applies the same
 * rule to the signed-in person, so hiding it here is a courtesy, not the control.
 */
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, UserCog } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { integrationService } from '@/services/api/integrationService'
import { useConfirm } from '@/hooks/useConfirm'

/** Where test personas exist; the integration service serves its persona routes in the same places. */
export const PERSONA_ENVIRONMENTS: readonly string[] = ['uat', 'staging', 'development', 'dev']

export const currentEnvironment = () => (import.meta.env.VITE_ENVIRONMENT || '').toLowerCase()

const EVERY_TESTER = '__every_tester__'

type SeedResult = Awaited<ReturnType<typeof integrationService.seedUATPersonas>>

const statusOf = (error: unknown): number | undefined => {
  const e = error as { response?: { status?: number }; status?: number } | undefined
  return e?.response?.status ?? e?.status
}

const refusal = (error: unknown): string => {
  switch (statusOf(error)) {
    case 401:
      return 'Your sign-in has expired. Sign in again and retry.'
    case 403:
      return 'Only admin and HR admin can reseed test personas.'
    case 404:
      return 'Test personas are not available in this environment.'
    default:
      return 'The test personas could not be reseeded. Try again in a moment.'
  }
}

export default function UatPersonaSeedAdmin() {
  const [target, setTarget] = useState<string>(EVERY_TESTER)
  const [result, setResult] = useState<{ at: Date; who: string; data: SeedResult } | null>(null)
  const { confirm } = useConfirm()

  const {
    data: participants,
    isLoading: loadingParticipants,
    error: participantsError,
  } = useQuery({
    queryKey: ['uat', 'participants'],
    queryFn: () => integrationService.getUATParticipants(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  const testers = participants?.participants ?? []
  const nameOf = (email?: string) => (email ? testers.find((p) => p.email === email)?.name || email : 'every tester')

  const seed = useMutation({
    mutationFn: (email?: string) => integrationService.seedUATPersonas(email),
    onSuccess: (data, email) => {
      setResult({ at: new Date(), who: nameOf(email), data })
      toast.success(`Test personas reseeded for ${nameOf(email)}`)
    },
    onError: (error) => toast.error(refusal(error)),
  })

  const reseed = async () => {
    const email = target === EVERY_TESTER ? undefined : target
    const proceed = await confirm(
      `This reseeds the test personas of ${nameOf(email)}. Missing personas are created, existing ones are ` +
        'refreshed from their definitions, including who reports to whom, and duplicates left by earlier ' +
        'runs are switched off. Testers can carry on once it finishes.',
      'Reseed test personas?',
      { confirmText: 'Reseed', type: 'warning' },
    )
    if (proceed) seed.mutate(email)
  }

  const counts = result
    ? [
        { label: 'Created', value: result.data.created },
        { label: 'Refreshed', value: result.data.updated },
        { label: 'Reporting lines set', value: result.data.manager_relationships_mapped },
        { label: 'Personas processed', value: result.data.total_processed },
      ]
    : []
  const problems = result?.data.errors ?? []

  return (
    <div className="space-y-4" data-demo="admin-test-personas">
      <Card className="p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-heritage-blue/10 p-2">
            <UserCog className="h-5 w-5 text-heritage-blue" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold">Test personas</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Testers switch into personas of their own to use the app as a team member, a People Leader or a
              One-Up. Reseed when a tester's personas are missing, out of date or report to the wrong person.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="persona-seed-target">Whose personas</Label>
            <Select value={target} onValueChange={setTarget} disabled={seed.isPending}>
              <SelectTrigger id="persona-seed-target">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={EVERY_TESTER}>Every tester</SelectItem>
                {testers.map((p) => (
                  <SelectItem key={p.id} value={p.email}>
                    {p.name} · {p.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {loadingParticipants && <p className="text-xs text-muted-foreground">Loading testers…</p>}
            {participantsError && <p className="text-xs text-red-600">{refusal(participantsError)}</p>}
          </div>
          <Button onClick={reseed} disabled={seed.isPending} data-demo="admin-test-personas-reseed">
            {seed.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            {seed.isPending ? 'Reseeding…' : 'Reseed test personas'}
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="p-5" data-demo="admin-test-personas-result">
          <div className="flex flex-wrap items-center gap-2">
            {problems.length > 0 ? (
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            ) : (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            )}
            <h3 className="font-semibold">Reseeded for {result.who}</h3>
            <span className="text-xs text-muted-foreground">
              {result.at.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {counts.map((c) => (
              <div key={c.label} className="rounded-md border p-3">
                <dt className="text-xs text-muted-foreground">{c.label}</dt>
                <dd className="text-xl font-semibold">{c.value}</dd>
              </div>
            ))}
          </dl>
          {problems.length > 0 && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-medium">
                {problems.length} {problems.length === 1 ? 'persona' : 'personas'} could not be completed
              </p>
              <ul className="mt-1 list-disc pl-5">
                {problems.map((p, i) => (
                  <li key={`${p.persona}-${i}`}>
                    {p.persona}: {p.error || p.relationship_error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
